'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FaqAccordion from './components/FaqAccordion';
import GoodList from './components/GoodList';
import { useFrenchSummerTracking } from './hooks/useFrenchSummerTracking';
import { SpringSaleClientProps, SpringSalePageData, CommodityVersion, FaqData } from './types';
import FrenchSummerBanner from './components/FrenchSummerBanner';
import CommodityLookbook from './components/CommodityLookbook';
import RelatedCategories from './components/RelatedCategories';
import ActivityProductGrid from '../components/ActivityProductGrid';
import { useRouter } from 'next/navigation';

export default function FrenchSummerClientPage({
  serverData,
  deviceType,
  commodityVersionList = [],
  faqData,
}: SpringSaleClientProps) {
  const [pageData, setPageData] = useState<SpringSalePageData>(serverData || {});

  // 当 serverData 改变时（如 router.refresh 后），同步更新 pageData
  useEffect(() => {
    if (serverData) {
      setPageData(serverData);
    }
  }, [serverData]);

  const [inAppH5, setInAppH5] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [allRecommendListLoaded, setAllRecommendListLoaded] = useState(false);
  const [isUserInfoReady, setIsUserInfoReady] = useState(false);

  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const isWap = deviceType !== EPlatform.WEB;
  const router = useRouter();

  // Banner曝光状态
  const [bannerExposed, setBannerExposed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // 使用服务端传递的数据，避免客户端重复请求
  const actualCommodityVersionList: CommodityVersion[] = useMemo(() => {
    return commodityVersionList.length > 0
      ? commodityVersionList
      : pageData?.commodity_version || [];
  }, [commodityVersionList, pageData?.commodity_version]);

  const actualFaqData: FaqData | null = useMemo(() => {
    return faqData || pageData?.faq || null;
  }, [faqData, pageData?.faq]);

  // 初始化埋点追踪
  const { trackPageView, trackPageLeave, trackProductListPitsClick, trackProductListPitsExposure } =
    useFrenchSummerTracking();

  // 检测是否在APP内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      setIsUserInfoReady(!isInApp); // 非App环境默认为准备完成
    }
  }, []);

  const userInfo = useStore((state) => state.userInfo);
  const prevEmailRef = useRef(userInfo?.email);

  // 监听登录状态变化，重新请求数据
  useEffect(() => {
    if (userInfo?.email && !prevEmailRef.current) {
      // 从未登录变为已登录，触发 router.refresh() 重新获取服务端数据
      router.refresh();
    }
    prevEmailRef.current = userInfo?.email;
  }, [userInfo?.email, router]);

  // 使用 ref 跟踪是否已经初始化，避免重复调用
  const hasInitialized = useRef(false);

  // App通信初始化
  useEffect(() => {
    if (inAppH5 && !hasInitialized.current) {
      // 处理App通信逻辑
      const handleAppCommunication = async () => {
        try {
          // 标记为已初始化
          hasInitialized.current = true;

          // 清空当前用户信息
          setToken('');
          setUserInfo({});

          // 注册token回调通信
          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('App登录token:', _token);
              if (_token !== 'ClearToken') {
                setToken(_token);
                // 获取用户信息
                await refreshAccountInfo();
                setTimeout(() => {
                  setIsUserInfoReady(true);
                }, 1000);
              } else {
                setIsUserInfoReady(true);
              }
            },
            {},
            'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
          // 初始化失败时重置标记，允许重试
          hasInitialized.current = false;
        }
      };

      handleAppCommunication();

      // APP内时监听appView和appLeave事件
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);

      // 添加超时机制，防止App通信失败导致页面一直加载
      const timeout = setTimeout(() => {
        if (!isUserInfoReady) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsUserInfoReady(true);
        }
      }, 5000); // 5秒超时

      return () => {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
        clearTimeout(timeout);
      };
    }
  }, [inAppH5]);

  // 组件卸载时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面加载完成后触发页面浏览埋点，组件卸载时触发离开埋点
  useEffect(() => {
    if (pageData) {
      // 只在WAP端：进入页面时隐藏底部导航，等推荐商品加载完成后再显示
      if (isWap) {
        setTimeout(() => {
          setIsShowFooterNav(false);
        }, 50);
      }
      trackPageView();
    }
    return () => {
      trackPageLeave();
    };
  }, [isWap]);

  // Banner曝光检测
  useEffect(() => {
    if (!bannerRef.current || bannerExposed || !pageData?.banner || pageData?.banner.length === 0)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !bannerExposed) {
            setBannerExposed(true);
          }
        });
      },
      { threshold: 0.5 }, // 当banner 50%可见时触发
    );

    observer.observe(bannerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [pageData?.banner, bannerExposed]);

  // 转换商品数据格式
  const transformProductData = (product: any) => {
    // 根据设备类型选取对应的价格信息，降级到默认 price_info
    const targetPriceInfo = isWap
      ? product.price_info_wap || product.price_info
      : product.price_info_pc || product.price_info;

    // 接口中预售商品可能是 status: 2, in_stock: '1'。这里统一转换为组件需要的 stock 字段
    const hasStock = product.in_stock === '1' || product.in_stock === 1 || product.stock > 0;

    return {
      ...product, // 保留其他所有属性
      id: product.id,
      name: product.name,
      short_name: product.short_name,
      shop_sku: product.shop_sku,
      business_sku: product.business_sku,
      image: product.image,
      price_info: targetPriceInfo,
      is_sold_out: !hasStock,
      stock: hasStock ? product.stock || 100 : 0, // 补充 stock 字段供下层组件判断
    };
  };

  // YouMightLike商品点击处理
  const handleYouMightLikeProductClick = useCallback(
    (product: any, index: number) => {
      trackProductListPitsClick(product, index, '猜你喜欢');
    },
    [trackProductListPitsClick],
  );

  // YouMightLike商品曝光处理
  const handleYouMightLikeProductExp = useCallback(
    (product: any, index: number) => {
      trackProductListPitsExposure(product, index, '猜你喜欢');
    },
    [trackProductListPitsExposure],
  );

  // 推荐商品加载完成回调
  const handleRecommendLoadComplete = useCallback(
    (hasMore?: boolean) => {
      setAllRecommendListLoaded(true);
      // 只在WAP端：推荐商品加载完成后才显示底部导航
      if (isWap) {
        setIsShowFooterNav(true);
      }
    },
    [isWap, setIsShowFooterNav],
  );

  // 提取所有商品用于 banner 查找
  const allProducts = useMemo(() => {
    const products: any[] = [];
    actualCommodityVersionList.forEach((group) => {
      const list = group.product_info_list || group.product_list || [];
      products.push(...list);
    });
    return products.map(transformProductData);
  }, [actualCommodityVersionList]);

  if (!pageData) {
    return null;
  }

  return (
    <Box className="w-full bg-[#ffffff]">
      {/* Banner区域 */}
      {pageData?.banner && (
        <div ref={bannerRef}>
          <FrenchSummerBanner
            data={pageData?.banner as any}
            deviceType={deviceType}
            allProducts={allProducts}
            pcAspectRatio={pageData?.pc_banner_aspectRatio}
            wapAspectRatio={pageData?.wap_banner_aspectRatio}
          />
        </div>
      )}

      {/* 商品区域 */}
      <div className="mt-8 md:mt-40">
        {/* 循环渲染 commodity_version 数组中的每个商品组 */}
        {actualCommodityVersionList.map((commodityGroup, validIndex) => {
          const commodityGroupMeta = commodityGroup as CommodityVersion & {
            name?: string;
            tab_name?: string;
            jump_type?: number | string;
            jump_value?: number | string;
            jump_content?: {
              jump_type?: number | string;
              Jump_target?: number | string;
              jump_target?: number | string;
              jump_value?: number | string;
            };
          };
          const groupTabName =
            commodityGroup.title?.text ||
            commodityGroupMeta.tab_name ||
            commodityGroupMeta.name ||
            String(commodityGroup.product_group_id ?? validIndex);
          const promoJumpTypeValue =
            commodityGroupMeta.jump_type ?? commodityGroupMeta.jump_content?.jump_type;
          const promoJumpValue =
            commodityGroupMeta.jump_value ??
            commodityGroupMeta.jump_content?.Jump_target ??
            commodityGroupMeta.jump_content?.jump_target ??
            commodityGroupMeta.jump_content?.jump_value;
          const promoJumpType =
            promoJumpTypeValue === undefined ? undefined : Number(promoJumpTypeValue);

          // 获取商品组的促销图片：优先使用商品组的image字段
          const getPromoImage = () => {
            if (commodityGroup.pc_image?.url) {
              return commodityGroup.pc_image.url;
            }
            return '';
          };

          return (
            <div key={`commodity-group-${validIndex}`}>
              <GoodList
                index={validIndex}
                tabName={groupTabName}
                moduleName={`商品版位-${validIndex + 1}`}
                promoImage={{
                  image: getPromoImage(),
                  wap_image: commodityGroup.wap_image?.url,
                  title: commodityGroup.title,
                  subtitle: commodityGroup.subtitle,
                  word: commodityGroup.title?.text,
                  image_alt: commodityGroup.image_alt || '',
                  description: commodityGroup.subtitle?.text,
                  aspectRatio: isWap
                    ? commodityGroup.wap_image?.aspectRatio
                    : commodityGroup.pc_image?.aspectRatio,
                  jump_type: Number.isNaN(promoJumpType) ? undefined : promoJumpType,
                  jump_value: promoJumpValue === undefined ? undefined : String(promoJumpValue),
                }}
                products={(
                  commodityGroup.product_info_list ||
                  commodityGroup.product_list ||
                  []
                ).map(transformProductData)}
                deviceType={deviceType}
                onProductClick={(product, productIndex) => {
                  // 触发商品列表坑位点击埋点
                  trackProductListPitsClick(
                    product,
                    productIndex,
                    `商品版位-${validIndex + 1}`,
                    groupTabName,
                  );
                }}
                onProductExp={(product, productIndex) => {
                  // 触发商品列表坑位曝光埋点
                  trackProductListPitsExposure(
                    product,
                    productIndex,
                    `商品版位-${validIndex + 1}`,
                    groupTabName,
                  );
                }}
                viewMoreConfig={{
                  groupId: commodityGroup.product_group_id?.toString() || '',
                  text: 'VIEW MORE',
                  showButton: true,
                }}
              />
            </div>
          );
        })}

        {/* 如果没有 commodity_version 数据，显示占位信息 */}
        {(!actualCommodityVersionList || actualCommodityVersionList.length === 0) && (
          <div className="w-full h-64 flex items-center justify-center bg-gray-100">
            <p className="text-gray-500">暂无商品数据</p>
          </div>
        )}
      </div>

      {/* 红人素材版位 */}
      {pageData.commodity_lookbook && pageData.commodity_lookbook.isShow && (
        <CommodityLookbook data={pageData.commodity_lookbook} deviceType={deviceType} />
      )}

      {/* YouMightLike猜你喜欢推荐商品 */}
      {pageData.you_may_also_like?.group_id && (
        <div className="pb-[20px]">
          <ActivityProductGrid
            title={
              <div className="pt-[20px] md:pt-[60px] lg:pt-[80px] pb-[20px] md:pb-[50px] mx-[16px] md:mx-[30px]">
                <h2 className="text-[24px] md:text-[32px] lg:text-[40px] font-bb text-center text-[#282828]">
                  {pageData.you_may_also_like?.title || 'You may also like'}
                </h2>
              </div>
            }
            apiConfig={{
              group_id: pageData.you_may_also_like.group_id,
              limit: 12,
              in_stock: 3,
              is_sale: 3,
            }}
            showInfiniteScroll
            onLoadComplete={(hasMore) => {
              setHasMore(hasMore);
              handleRecommendLoadComplete();
            }}
            padding={{ wap: 'px-[8px]', pc: 'px-[16px]' }}
            productCardProps={{
              showCorrelation: true,
            }}
            deviceType={deviceType}
            onProductClick={(product, index) => {
              handleYouMightLikeProductClick(product, index);
            }}
            onProductExp={(product, index) => {
              handleYouMightLikeProductExp(product, index);
            }}
          />
        </div>
      )}

      {/* 往期系列版位 */}
      {pageData.related_categories && pageData.related_categories.isShow && (
        <RelatedCategories data={pageData.related_categories} deviceType={deviceType} />
      )}

      {/* FAQ区域 - 只在推荐商品加载完成后显示 */}
      {allRecommendListLoaded && actualFaqData && (
        <div className="faq-section">
          <FaqAccordion faqData={{ ...actualFaqData, title: actualFaqData.title }} />
        </div>
      )}
    </Box>
  );
}
